import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Product, ProductPosition } from '@/api/products';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import {
  useDirectSalePositions,
  type DirectSalePositionEntry,
} from './useDirectSalePositions';
import { DirectSaleMockupPreview } from './DirectSaleMockupPreview';

/**
 * The views below the canvas: one control per sellable position, the active
 * one named. Which positions appear and how a switch happens lives in
 * useDirectSalePositions.
 */
export function DirectSalePositionPills({
  product,
  sellablePositions,
  variant = 'desktop',
}: {
  product: Product;
  sellablePositions: ProductPosition[];
  /**
   * 'desktop': a row of product thumbnails, see DesktopViews below.
   * 'phone': the views as dots under the product, after Spreadshirt —
   * arrows either side, the active view's name below, no prices, and
   * nothing for a single view. Thumbnails ate a row of the screen and
   * read as products to pick rather than sides to turn to.
   */
  variant?: 'desktop' | 'phone';
}) {
  const { t } = useTranslation();
  const masterPositions = useCanvasStateManager((s) => s.masterPositions);
  const { entries, activePosition, isSwitching, switchTo } =
    useDirectSalePositions(product, sellablePositions);

  const phone = variant === 'phone';
  if (entries.length === 0 || (phone && entries.length < 2)) return null;

  if (phone) {
    const activeIndex = Math.max(
      0,
      entries.findIndex((entry) => entry.position.slug === activePosition)
    );
    const step = (delta: number) => {
      const next =
        entries[(activeIndex + delta + entries.length) % entries.length];
      if (next) switchTo(next);
    };
    const arrow =
      'flex h-11 w-11 flex-none items-center justify-center text-ink disabled:opacity-40';

    return (
      <div className="flex items-center justify-center gap-1">
        <button
          type="button"
          aria-label={t('directSale.mobile.prevView')}
          disabled={isSwitching}
          onClick={() => step(-1)}
          className={arrow}
        >
          <ChevronLeft className="h-6 w-6" strokeWidth={2} />
        </button>
        <div className="flex min-w-[96px] flex-col items-center">
          <div className="flex items-center">
            {entries.map((entry, index) => {
              const active = index === activeIndex;
              return (
                <button
                  key={entry.position.id}
                  type="button"
                  aria-label={entry.position.name}
                  aria-pressed={active}
                  disabled={isSwitching}
                  onClick={() => switchTo(entry)}
                  // 28px tap target around a 6px dot.
                  className="flex h-7 w-5 items-center justify-center"
                >
                  <span
                    className={cn(
                      'block rounded-full',
                      active
                        ? 'h-3.5 w-3.5 border-2 border-ink'
                        : 'h-1.5 w-1.5 bg-ink'
                    )}
                  />
                </button>
              );
            })}
          </div>
          <span className="max-w-[160px] truncate text-[13px] font-medium text-ink">
            {entries[activeIndex]?.position.name}
          </span>
        </div>
        <button
          type="button"
          aria-label={t('directSale.mobile.nextView')}
          disabled={isSwitching}
          onClick={() => step(1)}
          className={arrow}
        >
          <ChevronRight className="h-6 w-6" strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <DesktopViews
      product={product}
      entries={entries}
      activePosition={activePosition}
      isSwitching={isSwitching}
      previews={masterPositions}
      onSwitch={switchTo}
    />
  );
}

/**
 * The desktop views, after Spreadshirt: a flat row of product thumbnails
 * with the active view's name below. No price per view — what printing a
 * side costs belongs to the price panel, and a row of amounts under the
 * product read as things to buy rather than sides to turn to.
 *
 * Its own component so the scroll effect's hooks stay out of the phone
 * branch above, which returns early.
 */
function DesktopViews({
  product,
  entries,
  activePosition,
  isSwitching,
  previews,
  onSwitch,
}: {
  product: Product;
  entries: DirectSalePositionEntry[];
  activePosition: string;
  isSwitching: boolean;
  previews: Record<string, { preview?: string | null } | undefined>;
  onSwitch: (entry: DirectSalePositionEntry) => void;
}) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const activeEntry = entries.find(
    (entry) => entry.position.slug === activePosition
  );

  // A view switched to from elsewhere (or one past the row's edge) must not
  // stay hidden in the overflow. `nearest` on both axes: never scroll the
  // page, and leave a view that is already visible where it is.
  useEffect(() => {
    const el = activeRef.current;
    // jsdom and old embedded browsers lack scrollIntoView.
    if (!el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activePosition]);

  return (
    <div className="flex flex-col items-center gap-1.5 px-4 pb-2.5">
      {/* Scrolls inside its own box: an overflowing row would widen the
          whole page. `mx-auto` rather than justify-center, so an
          overflowing row stays reachable from its left end. */}
      <div className="flex max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="mx-auto flex flex-none gap-2">
          {entries.map((entry) => {
            const { position } = entry;
            const active = position.slug === activePosition;

            return (
              <button
                key={position.id}
                ref={active ? activeRef : undefined}
                type="button"
                disabled={isSwitching}
                aria-pressed={active}
                aria-label={position.name}
                onClick={() => onSwitch(entry)}
                className={cn(
                  'flex h-[54px] w-[54px] flex-none items-center justify-center overflow-hidden rounded-control bg-paper',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  active
                    ? 'border-2 border-ink'
                    : 'border border-line hover:border-ink-3'
                )}
              >
                {/* Without a design yet the mockup shows the product alone
                    in the chosen colour — the real side, not a stand-in. */}
                <DirectSaleMockupPreview
                  product={product}
                  positionSlug={position.slug}
                  designDataUrl={previews[position.slug]?.preview ?? null}
                  fit={1}
                  lazyLoad
                  className="h-full w-full"
                />
              </button>
            );
          })}
        </div>
      </div>
      {activeEntry && (
        <span className="max-w-full truncate text-[13px] font-medium text-ink">
          {activeEntry.position.name}
        </span>
      )}
    </div>
  );
}
